// ExecMonthlyReport.js — "รายงานสรุปประจำเดือน/สัปดาห์" (pos 90/99)
//
// เอกสารสำหรับพิมพ์เข้าที่ประชุมหรือส่งต้นสังกัด ไม่ใช่หน้าสำหรับดูบนจอ
//
// ทุกเดือนมีคนนั่งรวมตัวเลขจากหลายฝ่ายด้วยมือ หน้านี้ทำให้ตัวเลขในที่ประชุมกับ
// ตัวเลขในระบบเป็นชุดเดียวกัน และเป็นเหตุผลให้ทุกฝ่ายกรอกข้อมูลให้ครบ
//
// รายสัปดาห์ใช้แบบฟอร์มและตัวเลขชุดเดียวกับรายเดือน เปลี่ยนแค่ช่วงวันที่ — ประชุม
// ฝ่ายรายสัปดาห์ถามเรื่องเดียวกับที่ประชุมประจำเดือนถาม แค่ถี่กว่า การทำเอกสารคนละ
// ชุดจะทำให้ตัวเลขสองใบไม่ตรงกันโดยไม่มีใครรู้ว่าใบไหนถูก
//
// เอกสารเป็นภาษาไทยเสมอ ไม่ตามภาษาเมนู เพราะเข้าแฟ้มของโรงเรียน — หลักเดียวกับ
// หนังสือสัญญาของระบบติดตามนักเรียน
import React, { useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Alert, Spinner } from "react-bootstrap";
import useSchoolSettings from "../hooks/useSchoolSettings";
import { useExecYear, useExecData, YearPicker, fmtNum, fmtPct, fmtBaht, toNum } from "./ExecShell";
import "../print.css";
import "./monthlyReport.css";

const THAI_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

const thaiMonthLabel = (month) => {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return "-";
  return `${THAI_MONTHS[m - 1]} ${y + 543}`;
};

// วันที่แบบเต็มสำหรับหัวเอกสาร — ประกอบเองจาก THAI_MONTHS ไม่ใช้ toLocaleDateString
// เพราะค่าที่ได้เป็นข้อความในเอกสารราชการ ต้องออกมาเหมือนกันทุกเครื่องทุกเบราว์เซอร์
const thaiFullDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return "-";
  return `${d} ${THAI_MONTHS[m - 1]} ${y + 543}`;
};

// ป้ายในตัวเลือกสัปดาห์ ย่อเดือนพอให้กดเลือกได้เร็ว — ตัวย่อเขียนไว้เอง ไม่ตัดจาก
// ชื่อเต็ม เพราะตัวย่อไทยไม่ได้ตัดตามตัวอักษร (พฤษภาคม ย่อเป็น พ.ค. ไม่ใช่ พฤษ)
const THAI_MONTHS_SHORT = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];

const weekLabel = (monday) => {
  const [y, m, d] = String(monday).split("-").map(Number);
  const end = new Date(y, m - 1, d + 6);
  return `${d} ${THAI_MONTHS_SHORT[m - 1]} – ${end.getDate()} ${THAI_MONTHS_SHORT[end.getMonth()]} ${end.getFullYear() + 543}`;
};

// ตัวเลือกเดือน 12 เดือนล่าสุด — ผู้บริหารพิมพ์ย้อนหลังได้ แต่ไม่ต้องเลือกปฏิทิน
const recentMonths = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

// ตัวเลือกสัปดาห์ 12 สัปดาห์ล่าสุด นับวันจันทร์เป็นวันแรกให้ตรงกับฝั่ง server
// (สัปดาห์ของโรงเรียนคือ จ.–ศ. ถ้าเริ่มวันอาทิตย์สัปดาห์เรียนจะถูกหั่นเป็นสองใบ)
const recentWeeks = () => {
  const out = [];
  const now = new Date();
  const dow = now.getDay(); // 0 = อาทิตย์
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (dow === 0 ? 6 : dow - 1));
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - i * 7);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  }
  return out;
};

// แถวหนึ่งของตาราง — ค่าว่างแสดงขีด ไม่ใช่ 0 เพราะ "ไม่มีข้อมูล" กับ "เป็นศูนย์"
// คนละเรื่องกันในเอกสารที่เข้าแฟ้ม
function Row2({ label, value, note }) {
  return (
    <tr>
      <td className="mr-label">{label}</td>
      <td className="mr-value">{value === null || value === undefined ? "—" : value}</td>
      <td className="mr-note">{note || ""}</td>
    </tr>
  );
}

// แกนความเสี่ยง เรียงตามลำดับที่ที่ประชุมถามถึง
//
// ป้ายชื่อในเอกสารบอกสิ่งที่วัดจริง ไม่ใช่ชื่อแกนบนหน้าจอ — แกน academic วัดงานค้างส่ง
// ไม่ใช่ผลการเรียน (ผลการเรียนอยู่ข้อ ๕) และ attendance วัดการเข้าเรียนรายคาบ ไม่ใช่
// การมาโรงเรียน คนอ่านเอกสารไม่ได้เปิดหน้าจอเทียบ จึงต้องอ่านแล้วเข้าใจตรงในตัวมันเอง
const RISK_AXES = [
  ["academic", "การส่งงาน"],
  ["attendance", "การเข้าเรียน"],
  ["behavior", "ความประพฤติ"],
  ["concern", "เรื่องกังวล"],
];

// "กลุ่มนี้ติดเพราะแกนไหน" — คำถามที่ตามมาทุกครั้งหลังบอกจำนวนคนในกลุ่ม
// นักเรียนที่ติดหลายแกนพร้อมกันถูกนับทุกแกน ผลรวมจึงมากกว่าจำนวนคนในกลุ่มได้
// (กฎการนับอยู่ที่ executiveRiskModel อธิบายไว้ในหมายเหตุท้ายเอกสาร)
// แกนที่ไม่มีใครไม่ต้องพิมพ์ — เอกสารที่มี "๐ คน" เรียงกันอ่านยากกว่า
const axisNote = (counts) => RISK_AXES
  .filter(([key]) => toNum(counts?.[key]) > 0)
  .map(([key, label]) => `${label} ${fmtNum(counts[key])}`)
  .join(" · ");

function Section({ title, children }) {
  return (
    <div className="mr-section">
      <div className="mr-section-title">{title}</div>
      <table className="mr-table">
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export default function ExecMonthlyReport() {
  const school = useSchoolSettings();
  const { years, yearId, setYearId } = useExecYear();
  const months = useMemo(recentMonths, []);
  const weeks = useMemo(recentWeeks, []);
  const [mode, setMode] = useState("month");
  const [month, setMonth] = useState(months[0]);
  const [week, setWeek] = useState(weeks[0]);

  // ส่ง mode ไปเสมอ แต่ส่งเฉพาะตัวที่ mode นั้นใช้ — ไม่งั้นเปลี่ยนเดือนตอนอยู่โหมด
  // รายสัปดาห์จะยิงคำขอใหม่ทั้งที่ผลเหมือนเดิม
  const query = useMemo(
    () => (mode === "week" ? { year_id: yearId, mode: "week", week } : { year_id: yearId, mode: "month", month }),
    [yearId, mode, month, week]
  );
  const { data, loading, error } = useExecData("/api/executive/monthly-report", query);

  const students = data?.students || {};
  const attendance = data?.attendance || {};
  const teaching = data?.teaching || {};
  const risk = data?.risk || {};
  const riskByLevel = risk.byLevel || {};
  // อ่านโหมดจากข้อมูลที่ได้กลับมา ไม่ใช่ state ของหน้า — ระหว่างรอคำขอใหม่ ตัวเลขที่
  // ยังค้างอยู่บนจอเป็นของงวดเดิม หัวเอกสารต้องตรงกับตัวเลขที่จะถูกพิมพ์ออกมา
  const weekly = data?.mode === "week";
  const conduct = data?.conduct || {};
  const homeLeave = data?.homeLeave || {};
  const staff = data?.staff || {};
  const academic = data?.academic || {};
  const fee = data?.fee;
  const dormitory = data?.dormitory;
  const issues = data?.issues;

  const studentDelta = students.previousTotal === null || students.previousTotal === undefined
    ? null
    : toNum(students.total) - toNum(students.previousTotal);

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-start justify-content-between mb-4 gap-3 d-print-none">
          <div>
            <label className="main-title-label">ตัวเลขและสถิติ</label>
            <h2 className="main-title mb-1"><i className="ri-printer-line me-2" />รายงานสรุปประจำเดือน/สัปดาห์</h2>
            <p className="text-secondary mb-0">เลือกเดือนหรือสัปดาห์แล้วสั่งพิมพ์ เอกสารเป็นภาษาไทยเสมอเพราะเข้าแฟ้มของโรงเรียน</p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mt-3 mt-sm-0">
            <Form.Select size="sm" style={{ width: "auto" }} value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="month">รายเดือน</option>
              <option value="week">รายสัปดาห์</option>
            </Form.Select>
            {mode === "week" ? (
              <Form.Select size="sm" style={{ width: "auto" }} value={week} onChange={(e) => setWeek(e.target.value)}>
                {weeks.map((w, i) => (
                  <option key={w} value={w}>{weekLabel(w)}{i === 0 ? " (สัปดาห์นี้)" : ""}</option>
                ))}
              </Form.Select>
            ) : (
              <Form.Select size="sm" style={{ width: "auto" }} value={month} onChange={(e) => setMonth(e.target.value)}>
                {months.map((m) => <option key={m} value={m}>{thaiMonthLabel(m)}</option>)}
              </Form.Select>
            )}
            <YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />
            <Button size="sm" variant="primary" onClick={() => window.print()} disabled={loading || !!error}>
              <i className="ri-printer-line me-1" />พิมพ์
            </Button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-5 d-print-none">
            <Spinner animation="border" variant="primary" />
            <div className="text-secondary mt-3">กำลังรวบรวมตัวเลข...</div>
          </div>
        )}
        {!loading && error && <Alert variant="danger" className="d-print-none">{error}</Alert>}

        {!loading && !error && data && (
          <Row className="justify-content-center">
            <Col xs={12} className="d-flex justify-content-center">
              <div className="mr-doc">
                <div className="mr-head">
                  <img src={school.logoSrc} alt="ตราโรงเรียน" className="mr-logo" />
                  <div className="mr-head-text">
                    <div className="mr-school">{school.school_name}</div>
                    <div className="mr-office">{school.school_address}</div>
                    <div className="mr-doc-title">
                      รายงานสรุปผลการดำเนินงาน{weekly ? "ประจำสัปดาห์" : "ประจำเดือน"}
                    </div>
                    <div className="mr-period">
                      {weekly
                        ? `ระหว่างวันที่ ${thaiFullDate(data.period?.from)} ถึง ${thaiFullDate(data.period?.to)}`
                        : `ประจำเดือน ${thaiMonthLabel(data.month)}`}
                      {" · "}ปีการศึกษา {data.year?.year_year || "-"}
                    </div>
                  </div>
                </div>

                <Section title="๑. จำนวนนักเรียน">
                  <Row2
                    label="นักเรียนทั้งหมด"
                    value={`${fmtNum(students.total)} คน`}
                    note={studentDelta === null ? "" : `${studentDelta >= 0 ? "เพิ่มขึ้น" : "ลดลง"} ${fmtNum(Math.abs(studentDelta))} คน จากปีก่อน`}
                  />
                  <Row2 label="แยกตามเพศ" value={`ชาย ${fmtNum(students.male)} คน · หญิง ${fmtNum(students.female)} คน`} />
                  <Row2 label="ภาคสามัญ" value={`${fmtNum(students.academic)} คน`} />
                  <Row2 label="ภาคศาสนา" value={`${fmtNum(students.islamic)} คน`} note="นับเป็นจำนวนคน ไม่ใช่ผลบวกของภาค" />
                  {toNum(students.ap) > 0 && <Row2 label="อนุบาล-ประถม" value={`${fmtNum(students.ap)} คน`} />}
                </Section>

                <Section title="๒. การมาเรียนและการจัดการเรียนการสอน">
                  <Row2 label="อัตราการมาเรียน" value={fmtPct(attendance.presentRate)} note={`จากการเช็คชื่อ ${fmtNum(attendance.records)} รายการ`} />
                  <Row2 label="อัตราการขาดเรียน" value={fmtPct(attendance.absentRate)} />
                  <Row2 label="จำนวนวันเปิดเรียน" value={`${fmtNum(attendance.schoolDays)} วัน`} />
                  <Row2
                    label="คาบที่ไม่มีการเช็คชื่อ"
                    value={`${fmtNum(teaching.missed)} คาบ (${fmtPct(teaching.missRate)})`}
                    note={`จากคาบที่มีตารางสอน ${fmtNum(teaching.slots)} คาบ · ครู ${fmtNum(teaching.teachersWithMiss)} คน`}
                  />
                </Section>

                <Section title="๓. การดูแลช่วยเหลือนักเรียน">
                  <Row2 label="นักเรียนกลุ่มเสี่ยงสูง" value={`${fmtNum(risk.red)} คน`} note={axisNote(riskByLevel.red)} />
                  <Row2 label="นักเรียนกลุ่มเสี่ยง" value={`${fmtNum(risk.orange)} คน`} note={axisNote(riskByLevel.orange)} />
                  <Row2 label="นักเรียนกลุ่มเฝ้าระวัง" value={`${fmtNum(risk.yellow)} คน`} note={axisNote(riskByLevel.yellow)} />
                  <Row2
                    label="รวมนักเรียนที่ต้องดูแล"
                    value={`${fmtNum(risk.atRisk)} คน (${fmtPct(risk.atRiskPercent)})`}
                    note={`จากนักเรียน ${fmtNum(risk.total)} คน`}
                  />
                  <Row2
                    label="ดำเนินการช่วยเหลือครบตามขั้นตอน"
                    value={`${fmtNum(risk.careComplete)} ราย`}
                    note={`ยังไม่ครบ ${fmtNum(risk.carePending)} ราย`}
                  />
                </Section>

                <Section title="๔. ความประพฤติและระเบียบวินัย">
                  <Row2 label="นักเรียนคะแนนความประพฤติต่ำกว่า ๗๐" value={`${fmtNum(conduct.low)} คน`} />
                  <Row2 label="นักเรียนคะแนนความประพฤติ ๗๐–๘๙" value={`${fmtNum(conduct.watch)} คน`} />
                  <Row2 label="คะแนนที่ถูกหักรวม" value={`${fmtNum(conduct.deducted)} คะแนน`} note={`ได้คืนจากงานจิตอาสา ${fmtNum(conduct.merited)} คะแนน`} />
                  <Row2 label="ใบขออนุญาตกลับบ้าน" value={`${fmtNum(homeLeave.requests)} ฉบับ`} note={toNum(homeLeave.overdue) > 0 ? `เลยกำหนดกลับ ${fmtNum(homeLeave.overdue)} ราย` : ""} />
                </Section>

                <Section title="๕. งานวิชาการ">
                  {academic.gradesFinal ? (
                    <>
                      {(academic.gpaByClass || []).map((row) => (
                        <Row2
                          key={row.c_class}
                          label={`ผลการเรียนเฉลี่ย ${row.c_class}`}
                          value={row.avg_gpa === null ? "—" : row.avg_gpa.toFixed(2)}
                          note={`นักเรียน ${fmtNum(row.students)} คน · ต่ำกว่า ๒.๐๐ จำนวน ${fmtNum(row.weak)} คน`}
                        />
                      ))}
                    </>
                  ) : (
                    <Row2 label="ผลการเรียน" value="ยังไม่ได้ตัดเกรด" note="อยู่ระหว่างภาคเรียน ครูกำลังบันทึกคะแนนเก็บ" />
                  )}
                  <Row2 label="วิชาที่ส่งผลการเรียนครบ" value={fmtPct(academic.gradeCompletePercent)} />
                  <Row2 label="วิชาที่กำหนดคะแนนเก็บแล้ว" value={fmtPct(academic.scoreItemPercent)} />
                  <Row2 label="นักเรียนที่มีงานค้างส่ง" value={`${fmtNum(academic.pendingWorkStudents)} คน`} />
                </Section>

                <Section title="๖. บุคลากร">
                  <Row2 label="บุคลากรทั้งหมด" value={`${fmtNum(staff.total)} คน`} />
                  <Row2
                    label={weekly ? "วันลารวมในสัปดาห์นี้" : "วันลารวมในเดือนนี้"}
                    value={`${fmtNum(staff.leaveDays)} วัน`}
                    note={(staff.leaveByType || []).map((t) => `${t.name} ${fmtNum(t.days)} วัน`).join(" · ")}
                  />
                </Section>

                {(fee || dormitory || issues) && (
                  <Section title="๗. งานสนับสนุน">
                    {fee && (
                      <>
                        <Row2 label="ยอดค่าธรรมเนียมที่เรียกเก็บ" value={fmtBaht(fee.total)} />
                        <Row2 label="เก็บได้แล้ว" value={`${fmtBaht(fee.paid)} (${fmtPct(fee.collectionRate)})`} note={`ค้างชำระ ${fmtBaht(fee.outstanding)}`} />
                      </>
                    )}
                    {dormitory && (
                      <Row2
                        label="อัตราการเข้าพักหอพัก"
                        value={fmtPct(dormitory.occupancyRate)}
                        note={`${fmtNum(dormitory.occupied)} คน จากความจุ ${fmtNum(dormitory.capacity)} เตียง`}
                      />
                    )}
                    {issues && (
                      <Row2
                        label="เรื่องแจ้งซ่อม/ร้องเรียน"
                        value={`รับแจ้ง ${fmtNum(issues.total)} เรื่อง`}
                        note={`ดำเนินการแล้วเสร็จ ${fmtNum(issues.done)} เรื่อง · ค้างเกินกำหนด ${fmtNum(issues.overdue)} เรื่อง`}
                      />
                    )}
                  </Section>
                )}

                <div className="mr-sign">
                  <div className="mr-sign-box">
                    <div className="mr-sign-line" />
                    <div>({school.principal_name})</div>
                    <div>ผู้อำนวยการ{school.school_name}</div>
                  </div>
                </div>

                <div className="mr-footnote">
                  ตัวเลขทั้งหมดดึงจากระบบสารสนเทศของโรงเรียน ณ วันที่จัดพิมพ์
                  ช่วงข้อมูล {data.period?.from} ถึง {data.period?.to}
                  {/* รายงานรายสัปดาห์ทำให้ความต่างนี้สำคัญขึ้นมาก ถ้าไม่บอกไว้จะถูกอ่าน
                      ว่าสัปดาห์เดียวมีนักเรียนกลุ่มเสี่ยงเพิ่มขึ้นเป็นร้อยคน */}
                  <div>
                    ข้อ ๒ (การมาเรียน การเช็คชื่อของครู) ข้อ ๔ เฉพาะใบขออนุญาตกลับบ้าน ข้อ ๖ เฉพาะวันลา
                    และเรื่องแจ้งซ่อม เป็นตัวเลขเฉพาะช่วงข้างต้น ส่วนหัวข้ออื่นเป็นยอดสะสมของปีการศึกษา
                    ณ วันที่จัดพิมพ์
                  </div>
                  <div>
                    การแยกแกนในข้อ ๓ นับตามแกนที่ทำให้นักเรียนอยู่ในกลุ่มนั้น
                    นักเรียนที่ติดหลายแกนพร้อมกันถูกนับทุกแกน ผลรวมรายแกนจึงมากกว่าจำนวนนักเรียนในกลุ่มได้
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}

        <div className="d-print-none"><Footer /></div>
      </div>
    </React.Fragment>
  );
}
